import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import LayersOutlineIcon from 'mdi-react/LayersOutlineIcon';
import HistoryIcon from 'mdi-react/HistoryIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import AutoFixIcon from 'mdi-react/AutoFixIcon';
import FileImportOutlineIcon from 'mdi-react/FileImportOutlineIcon';
import ClipboardCheckOutlineIcon from 'mdi-react/ClipboardCheckOutlineIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import MenuOpenIcon from 'mdi-react/MenuOpenIcon';
import { editorTheme, sectionHeadingSx } from '../utils/editor-theme';

export type LeftRailTab = 'layers' | 'import' | 'history' | 'find' | 'automate' | 'validate';

interface Props {
  /** The open section, or null when every section is closed and the canvas has the column. */
  readonly tab: LeftRailTab | null;
  readonly onTabChange: (tab: LeftRailTab | null) => void;
  readonly collapsed: boolean;
  readonly onCollapsedChange: (collapsed: boolean) => void;
  /** The open panel. Chosen by the editor, so the rail itself knows nothing about a floorplan. */
  readonly children: React.ReactNode;
}

const SECTIONS: ReadonlyArray<{
  readonly value: LeftRailTab;
  readonly labelKey: string;
  readonly Icon: typeof LayersOutlineIcon;
}> = [
  { value: 'layers', labelKey: 'floorplan-editor-panel-layers', Icon: LayersOutlineIcon },
  { value: 'import', labelKey: 'floorplan-editor-panel-import', Icon: FileImportOutlineIcon },
  { value: 'history', labelKey: 'floorplan-editor-panel-history', Icon: HistoryIcon },
  { value: 'find', labelKey: 'floorplan-editor-panel-find', Icon: MagnifyIcon },
  { value: 'automate', labelKey: 'floorplan-editor-panel-automate', Icon: AutoFixIcon },
  { value: 'validate', labelKey: 'floorplan-editor-panel-validate', Icon: ClipboardCheckOutlineIcon },
];

/** Narrower than the old tabbed column, because the canvas is what the column is taken from. */
const RAIL_WIDTH = 264;
const COLLAPSED_WIDTH = 44;

/**
 * The editor's left-hand column: layers, import, history, scale, find, automation, validation.
 *
 * An accordion rather than a row of tabs. Seven tabs in 288 pixels leaves each one an
 * unlabelled 18-pixel icon, which is a guessing game — and the panels are not peers an admin
 * switches between constantly, they are things reached for occasionally and left closed the
 * rest of the time. Stacked and named, the column says what the editor can do; open, it
 * gives the one panel in use the whole height.
 *
 * One section at a time, and closing it leaves seven header rows and the rest of the height
 * to nothing — which is the point, because the alternative is an admin choosing between the
 * panel they need and the canvas they are working on.
 *
 * Knows nothing about floorplans. The editor renders the open panel into it, which is what
 * keeps the section that is open and the panel that is showing from ever disagreeing.
 */
const EditorLeftRail = ({
  tab,
  onTabChange,
  collapsed,
  onCollapsedChange,
  children,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();

  /** Opens a section, expanding the rail first when it was collapsed to its icons. */
  const open = (value: LeftRailTab): void => {
    onCollapsedChange(false);
    onTabChange(value);
  };

  if (collapsed) {
    return (
      <Stack
        alignItems="center"
        sx={{
          width: COLLAPSED_WIDTH,
          flexShrink: 0,
          py: 0.5,
          borderRight: `1px solid ${editorTheme.line}`,
          backgroundColor: editorTheme.surface,
        }}
        aria-label={String(t('floorplan-editor-panels'))}
      >
        <Tooltip title={String(t('floorplan-editor-panel-expand'))} placement="right">
          <IconButton
            size="small"
            onClick={(): void => onCollapsedChange(false)}
            aria-label={String(t('floorplan-editor-panel-expand'))}
            aria-expanded={false}
            sx={{ color: editorTheme.inkDim }}
          >
            <MenuOpenIcon size={18} style={{ transform: 'scaleX(-1)' }} />
          </IconButton>
        </Tooltip>

        <Box sx={{ width: 24, my: 0.5, borderTop: `1px solid ${editorTheme.lineSoft}` }} />

        {/* The sections stay reachable while collapsed. A panel with no visible way back is a
            panel people stop using. */}
        {SECTIONS.map(({ value, labelKey, Icon }) => (
          <Tooltip key={value} title={String(t(labelKey))} placement="right">
            <IconButton
              size="small"
              onClick={(): void => open(value)}
              aria-label={String(t(labelKey))}
              sx={{ my: 0.125, color: tab === value ? editorTheme.accent : editorTheme.inkFaint }}
            >
              <Icon size={18} />
            </IconButton>
          </Tooltip>
        ))}
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        width: RAIL_WIDTH,
        flexShrink: 0,
        minHeight: 0,
        borderRight: `1px solid ${editorTheme.line}`,
        backgroundColor: editorTheme.surface,
        overflowY: 'auto',
      }}
    >
      {SECTIONS.map(({ value, labelKey, Icon }) => {
        const isOpen = tab === value;
        const label = String(t(labelKey));

        return (
          <Stack
            key={value}
            sx={{
              minHeight: 0,
              // Only the open section takes the height it needs; the closed ones are a header
              // apiece, so the whole list stays visible above and below whatever is open.
              flex: isOpen ? '1 1 auto' : '0 0 auto',
              borderBottom: `1px solid ${editorTheme.lineSoft}`,
            }}
          >
            <Stack
              component="button"
              type="button"
              direction="row"
              alignItems="center"
              spacing={1}
              // Clicking the open section closes it, which is how the column is given back to
              // the canvas without collapsing the rail down to icons.
              onClick={(): void => onTabChange(isOpen ? null : value)}
              aria-expanded={isOpen}
              aria-controls={`floorplan-panel-${value}`}
              sx={{
                px: 1.25,
                py: 1,
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                color: isOpen ? editorTheme.ink : editorTheme.inkDim,
                backgroundColor: isOpen ? editorTheme.surfaceSunken : 'transparent',
                // A bar down the left edge of the open section, so the column can be read at a
                // glance rather than by finding which chevron is pointing down.
                boxShadow: isOpen ? `inset 3px 0 0 ${editorTheme.accent}` : 'none',
                '&:hover': { backgroundColor: editorTheme.surfaceSunken },
                '&:focus-visible': { outline: `2px solid ${editorTheme.accent}`, outlineOffset: -2 },
              }}
            >
              {isOpen ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
              <Icon size={16} style={{ color: isOpen ? editorTheme.accent : editorTheme.inkFaint }} />
              <Typography component="span" sx={{ ...sectionHeadingSx, color: 'inherit' }}>
                {label}
              </Typography>
            </Stack>

            {/* Unmounted rather than hidden when closed: a panel that keeps polling or holds a
                half-filled form behind a closed header is a surprise nobody asked for. Scrolls
                inside its own box, so a long layer list never pushes the headers below it off
                the bottom of the column. */}
            {isOpen && (
              <Box
                id={`floorplan-panel-${value}`}
                role="region"
                aria-label={label}
                sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}
              >
                {children}
              </Box>
            )}
          </Stack>
        );
      })}

      <Stack direction="row" justifyContent="flex-end" sx={{ p: 0.5, mt: 'auto' }}>
        <Tooltip title={String(t('floorplan-editor-panel-collapse'))}>
          <IconButton
            size="small"
            onClick={(): void => onCollapsedChange(true)}
            aria-label={String(t('floorplan-editor-panel-collapse'))}
            aria-expanded
            sx={{ color: editorTheme.inkDim }}
          >
            <MenuOpenIcon size={18} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

export default EditorLeftRail;
